import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate } from "@/pages/admin/util";
import { Loader2, RefreshCw, Plus, Pencil, Trash2, Trees, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  slug: "",
  operator_id: "",
  county: "",
  town: "",
  postcode_area: "",
  residential: true,
  holiday: false,
  pitches_count: "",
  summary: "",
  key_facts: "",
  website: "",
  status: "PUBLISHED",
};

function toForm(p) {
  if (!p) return { ...EMPTY };
  return {
    name: p.name || "",
    slug: p.slug || "",
    operator_id: p.operator_id || "",
    county: p.county || "",
    town: p.town || "",
    postcode_area: p.postcode_area || "",
    residential: p.residential !== false,
    holiday: !!p.holiday,
    pitches_count: p.pitches_count ?? "",
    summary: p.summary || "",
    key_facts: (p.key_facts || []).join("\n"),
    website: p.website || "",
    status: p.status || "PUBLISHED",
  };
}

function toPayload(form) {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    operator_id: form.operator_id || "",
    county: form.county.trim(),
    town: form.town.trim(),
    postcode_area: form.postcode_area.trim(),
    residential: !!form.residential,
    holiday: !!form.holiday,
    pitches_count: form.pitches_count === "" ? null : Number(form.pitches_count),
    summary: form.summary.trim(),
    key_facts: form.key_facts.split("\n").map((s) => s.trim()).filter(Boolean),
    website: form.website.trim(),
    status: form.status,
  };
}

export default function AdminIntelligenceParks() {
  const [items, setItems] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [parksRes, opsRes] = await Promise.all([
        adminApi.get("/parks"),
        adminApi.get("/park-operators"),
      ]);
      setItems(parksRes.data.items || []);
      setOperators(opsRes.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load parks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(p) {
    if (!confirm(`Delete park "${p.name}"?`)) return;
    try {
      await adminApi.delete(`/parks/${p.id}`);
      toast.success("Park deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  const filtered = q.trim()
    ? items.filter((p) => {
        const t = q.toLowerCase();
        return (p.name || "").toLowerCase().includes(t)
          || (p.county || "").toLowerCase().includes(t)
          || (p.town || "").toLowerCase().includes(t)
          || (p.operator_name || "").toLowerCase().includes(t);
      })
    : items;

  return (
    <div className="space-y-6" data-testid="admin-intel-parks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Intelligence platform</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Parks Directory</h1>
          <p className="text-sm text-[#475569] mt-1 max-w-2xl">Internal directory of real residential and holiday parks — used during quote reviews, support cases and operator intel.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="parks-refresh-btn">
            <RefreshCw size={14}/> Refresh
          </button>
          <button onClick={() => setEditing("new")} className="btn-primary h-10 py-0 px-4 text-sm" data-testid="parks-new-btn">
            <Plus size={14}/> New park
          </button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"/>
        <input
          className="field-input pl-9 m-0"
          placeholder="Filter by name, county, town, operator…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="parks-filter-input"
        />
      </div>

      <div className="card-default p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.18em]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Operator</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="inline animate-spin" size={16}/></td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#475569]">No parks yet. Add real, verifiable parks only.</td></tr>
            )}
            {!loading && filtered.map((p) => (
              <tr key={p.id} data-testid={`park-row-${p.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#071A33] flex items-center gap-2"><Trees size={14} className="text-[#064E2E]"/>{p.name}</div>
                  <div className="text-xs text-[#94A3B8]">/{p.slug}</div>
                </td>
                <td className="px-4 py-3 text-[#334155]">{[p.town, p.county].filter(Boolean).join(", ")}</td>
                <td className="px-4 py-3 text-[#334155]">{p.operator_name || "—"}</td>
                <td className="px-4 py-3 text-xs">
                  {p.residential && <span className="bg-[#064E2E]/10 text-[#064E2E] px-1.5 py-0.5 rounded mr-1">Residential</span>}
                  {p.holiday && <span className="bg-[#B88A2A]/15 text-[#B88A2A] px-1.5 py-0.5 rounded">Holiday</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] tracking-[0.1em] uppercase ${p.status === "PUBLISHED" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#475569]/15 text-[#475569]"}`}>{p.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#475569]">{fmtDate(p.updated_at, true)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-[#F7F4ED]"><ExternalLink size={14} className="text-[#475569]"/></a>
                    )}
                    <button onClick={() => setEditing(p)} className="p-2 rounded-full hover:bg-[#F7F4ED]" data-testid={`park-edit-${p.slug}`}><Pencil size={14} className="text-[#0F766E]"/></button>
                    <button onClick={() => remove(p)} className="p-2 rounded-full hover:bg-[#FCA5A5]/20"><Trash2 size={14} className="text-[#DC2626]"/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ParkDialog
          initial={editing === "new" ? null : editing}
          operators={operators}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ParkDialog({ initial, operators, onClose, onSaved }) {
  const [form, setForm] = useState(toForm(initial));
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.county.trim()) {
      toast.error("Name and county are required");
      return;
    }
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (initial) {
        await adminApi.put(`/parks/${initial.id}`, payload);
        toast.success("Park updated");
      } else {
        await adminApi.post("/parks", payload);
        toast.success("Park created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4 overflow-y-auto" data-testid="park-dialog">
      <form onSubmit={submit} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl my-6">
        <div className="px-6 py-5 border-b border-[#E2DCD0] flex items-center gap-3">
          <Trees size={20} className="text-[#064E2E]"/>
          <h2 className="font-display text-2xl text-[#071A33]">{initial ? "Edit park" : "New park"}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Park name *</label>
              <input className="field-input" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="park-name"/>
            </div>
            <div>
              <label className="field-label">Slug <span className="text-[#94A3B8] font-normal">(auto if blank)</span></label>
              <input className="field-input" value={form.slug} onChange={(e) => update("slug", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">County *</label>
              <input className="field-input" value={form.county} onChange={(e) => update("county", e.target.value)} required data-testid="park-county"/>
            </div>
            <div>
              <label className="field-label">Town</label>
              <input className="field-input" value={form.town} onChange={(e) => update("town", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Postcode area <span className="text-[#94A3B8] font-normal">(e.g. CM7)</span></label>
              <input className="field-input" value={form.postcode_area} onChange={(e) => update("postcode_area", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Operator</label>
              <select className="field-input" value={form.operator_id} onChange={(e) => update("operator_id", e.target.value)}>
                <option value="">— Independent / unknown —</option>
                {operators.map((op) => (
                  <option key={op.id} value={op.id}>{op.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Pitches (est.)</label>
              <input className="field-input" type="number" min="0" value={form.pitches_count} onChange={(e) => update("pitches_count", e.target.value)}/>
            </div>
            <div>
              <label className="field-label">Website</label>
              <input className="field-input" type="url" value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://"/>
            </div>
            <div className="flex items-center gap-5 sm:col-span-2 pt-2">
              <label className="flex items-center gap-2 text-sm text-[#334155]"><input type="checkbox" checked={form.residential} onChange={(e) => update("residential", e.target.checked)} className="accent-[#064E2E]"/> Residential (12-month)</label>
              <label className="flex items-center gap-2 text-sm text-[#334155]"><input type="checkbox" checked={form.holiday} onChange={(e) => update("holiday", e.target.checked)} className="accent-[#064E2E]"/> Holiday</label>
            </div>
          </div>
          <div>
            <label className="field-label">Summary</label>
            <textarea className="field-input min-h-[100px]" value={form.summary} onChange={(e) => update("summary", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Key facts <span className="text-[#94A3B8] font-normal">(one per line)</span></label>
            <textarea className="field-input min-h-[80px]" value={form.key_facts} onChange={(e) => update("key_facts", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Status</label>
            <select className="field-input" value={form.status} onChange={(e) => update("status", e.target.value)}>
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Draft</option>
            </select>
          </div>
        </div>
        <div className="px-6 py-4 bg-[#F7F4ED] border-t border-[#E2DCD0] flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-outline" disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="park-save-btn">
            {busy ? <><Loader2 className="animate-spin" size={16}/> Saving…</> : initial ? "Save changes" : "Create park"}
          </button>
        </div>
      </form>
    </div>
  );
}
